import React, { useState, useEffect, useMemo } from 'react';
import {
  Row, Col, Card, Statistic, Spin, Table, Alert, Segmented, Space, Tag,
  Tooltip as AntTooltip,
} from 'antd';
import {
  BarChart, Bar, LineChart, Line, ScatterChart, Scatter, Cell,
  XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import dayjs from 'dayjs';
import api from '../../libs/api';

/**
 * What PW Renewable is holding, where, and what it costs to hold it.
 *
 * Two halves on two different footings, and the page says so rather than
 * blending them. Unleashed reports stock as it stands and will not report it
 * as it stood — `asAtDate` is accepted and ignored, there is no movement
 * endpoint, and transfers between sites are not exposed at all — so the live
 * figures here are exact and have no past.
 *
 * The history comes instead from the 3PL fee workbook, which records average
 * inventory per invoice period per site. It is kept by hand, it is what the
 * warehouse contracts are measured against, and it is the only figure the
 * storage fees can honestly be divided by.
 */

const INK = '#262B40';
const NEUTRAL = '#667085';
const FAINT = '#9AA3B2';
const GRID = '#EEF1F5';

/**
 * One hue per warehouse, fixed. Four sites that never change, so the colour
 * can be an identity rather than a position in a list.
 */
const WH_COLOR = {
  W1: '#2F7FA8',
  W2: '#C96A1E',
  W3: '#5B8C72',
  W4: '#7B5EA7',
};

/** Cost buckets, ordered cheapest-to-read first: what drives them differs. */
const BUCKETS = [
  ['storage', 'Storage', '#2F7FA8'],
  ['handling', 'Handling', '#C96A1E'],
  ['freight', 'Freight', '#5B8C72'],
  ['other', 'Other', '#9AA3B2'],
];

/**
 * A log axis wants powers of ten, not one tick per data point.
 *
 * Left to pick its own, recharts labelled every distinct value on the axis —
 * four hundred overlapping day counts rendered as a grey smear where the
 * scale should have been. Snapped to decades the axis says 1, 10, 100, 1000,
 * which is the whole reason for choosing a log scale.
 */
const floorPow10 = (v) => 10 ** Math.floor(Math.log10(Math.max(v, 1e-9)));
const ceilPow10 = (v) => 10 ** Math.ceil(Math.log10(Math.max(v, 1e-9)));
const pow10Ticks = (lo, hi) => {
  const out = [];
  for (let e = Math.round(Math.log10(floorPow10(lo))); e <= Math.round(Math.log10(ceilPow10(hi))); e += 1) {
    out.push(10 ** e);
  }
  return out;
};

/**
 * The same charges collapsed to what causes them.
 *
 * Four buckets say what the provider called it; two say what made it happen.
 * Storage-related accrues while stock sits, handling-related when it moves,
 * and a bill that is mostly one of them is a different contract from a bill
 * that is mostly the other.
 */
const DRIVERS = [
  ['storageRelated', 'Storage-related', '#2F7FA8'],
  ['handlingRelated', 'Handling-related', '#C96A1E'],
  ['freightRelated', 'Freight', '#5B8C72'],
];

const money = (v) => (v == null ? '—' : `$${Math.round(v).toLocaleString()}`);
const moneyShort = (v) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  if (a >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (a >= 1000) return `$${Math.round(v / 1000)}k`;
  return `$${Math.round(v)}`;
};

function Panel({ title, rows, footer }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #E4E9F0', borderRadius: 6,
      padding: '8px 10px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
    >
      <div style={{ fontWeight: 600, color: INK, marginBottom: 6 }}>{title}</div>
      {rows.filter((r) => r.value != null).map((r) => (
        <div key={r.label} style={{ display: 'flex', gap: 16, justifyContent: 'space-between' }}>
          <span style={{ color: r.color || NEUTRAL }}>{r.label}</span>
          <span style={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>{r.value}</span>
        </div>
      ))}
      {footer && <div style={{ color: FAINT, marginTop: 6, maxWidth: 240 }}>{footer}</div>}
    </div>
  );
}

export default function Inventory() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [wh, setWh] = useState('all');
  const [ratioMode, setRatioMode] = useState('inventory');
  const [costMode, setCostMode] = useState('driver');

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get('/api/distribution/inventory', { timeout: 180000 })
      .then((r) => { if (live) { setData(r.data?.data || null); setError(null); } })
      .catch((e) => { if (live) setError(e.response?.data?.message || e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  const warehouses = useMemo(() => data?.warehouses || [], [data]);
  const stock = useMemo(() => data?.stock || [], [data]);
  const opex = useMemo(() => data?.opex || [], [data]);

  const rows = useMemo(
    () => (wh === 'all' ? stock : stock.filter((s) => s.warehouseCode === wh)),
    [stock, wh],
  );

  const totals = useMemo(() => {
    const dead = rows.filter(
      (s) => s.daysSinceLastSale != null && s.daysSinceLastSale > (data?.deadStockDays || 180),
    );
    return {
      skus: rows.length,
      units: rows.reduce((t, s) => t + s.qtyOnHand, 0),
      value: rows.reduce((t, s) => t + s.totalCost, 0),
      allocated: rows.reduce((t, s) => t + s.allocated, 0),
      onPurchase: rows.reduce((t, s) => t + s.onPurchase, 0),
      deadSkus: dead.length,
      deadValue: dead.reduce((t, s) => t + s.totalCost, 0),
    };
  }, [rows, data]);

  /** Top 15 by value, which is the only ranking that pays for shelf space. */
  const byProduct = useMemo(() => {
    const m = new Map();
    for (const s of rows) {
      const e = m.get(s.productCode) || { code: s.productCode, name: s.description, value: 0, qty: 0 };
      e.value += s.totalCost;
      e.qty += s.qtyOnHand;
      m.set(s.productCode, e);
    }
    return [...m.values()].sort((a, b) => b.value - a.value).slice(0, 15);
  }, [rows]);

  /**
   * Days since last sale against value held.
   *
   * Both axes are logarithmic because both span five orders of magnitude, and
   * a linear axis would stack nine tenths of the catalogue against the origin.
   * Zero cannot be plotted on a log scale, so stock with no value and products
   * that have never sold are left out rather than pinned to an invented floor.
   */
  const scatter = useMemo(() => rows
    .filter((s) => s.totalCost > 0 && s.daysSinceLastSale > 0)
    .map((s) => ({
      x: s.daysSinceLastSale,
      y: s.totalCost,
      code: s.productCode,
      name: s.description,
      qty: s.qtyOnHand,
      warehouse: s.warehouse,
      // Green turns, amber is slowing, red is sitting. The thresholds are the
      // ones the Sales tab already used, kept so the two read the same way.
      fill: s.daysSinceLastSale < 30 ? '#52A373'
        : (s.daysSinceLastSale <= 100 ? '#E0A800' : '#D9534F'),
    })), [rows]);

  const scatterAxes = useMemo(() => {
    if (!scatter.length) return null;
    const xs = scatter.map((d) => d.x);
    const ys = scatter.map((d) => d.y);
    const x = [floorPow10(Math.min(...xs)), ceilPow10(Math.max(...xs))];
    const y = [floorPow10(Math.min(...ys)), ceilPow10(Math.max(...ys))];
    return { x, y, xTicks: pow10Ticks(x[0], x[1]), yTicks: pow10Ticks(y[0], y[1]) };
  }, [scatter]);

  /** 3PL periods for the selected site, in sheet order, which is chronological. */
  const opexRows = useMemo(
    () => (wh === 'all' ? opex : opex.filter((r) => r.warehouseCode === wh)),
    [opex, wh],
  );

  /**
   * Cost by calendar month, which is the only axis the three contracts share.
   *
   * They invoice weekly, monthly and on a four-week cycle, so their own
   * periods cannot be put side by side. Summed into months they can — a month
   * is a month at every site — and a weekly biller simply contributes four or
   * five rows to each point.
   */
  const months = useMemo(() => {
    const m = new Map();
    for (const r of opexRows) {
      if (!r.date) continue;
      const key = r.date.slice(0, 7);
      const e = m.get(key) || {
        key, storage: 0, handling: 0, freight: 0, other: 0, total: 0,
        storageRelated: 0, handlingRelated: 0, freightRelated: 0,
        avgInventory: 0, cogsSold: 0, orders: 0, periods: 0, inferred: false,
      };
      for (const [k] of BUCKETS) e[k] += r[k] || 0;
      for (const [k] of DRIVERS) e[k] += r[k] || 0;
      e.total += r.total || 0;
      e.cogsSold += r.cogsSold || 0;
      e.orders += r.orders || 0;
      // Inventory is a level, not a flow: four weekly readings of what was
      // held average to the month, they do not add up to it.
      e.avgInventory += r.avgInventory || 0;
      e.periods += 1;
      e.inferred = e.inferred || r.dateInferred;
      m.set(key, e);
    }
    return [...m.values()]
      .map((e) => ({
        ...e,
        avgInventory: e.periods ? e.avgInventory / e.periods : 0,
        label: dayjs(`${e.key}-01`).format('MMM YY'),
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [opexRows]);

  /**
   * The same cost divided by what drove it, one warehouse per line.
   *
   * Storage is paid on what is held and handling on what goes out, so a single
   * total has two different denominators and the right one depends on the
   * question. Side by side the two would need two axes; as a toggle they share
   * one and stay comparable.
   */
  const ratioSeries = useMemo(() => {
    const sites = warehouses.filter(
      (w) => w.thirdParty && (wh === 'all' || w.code === wh),
    );
    const keys = new Set();
    const per = {};
    for (const w of sites) {
      const m = new Map();
      for (const r of opex.filter((o) => o.warehouseCode === w.code && o.date)) {
        const key = r.date.slice(0, 7);
        const e = m.get(key) || { total: 0, avgInventory: 0, cogsSold: 0, n: 0 };
        e.total += r.total || 0;
        e.cogsSold += r.cogsSold || 0;
        e.avgInventory += r.avgInventory || 0;
        e.n += 1;
        m.set(key, e);
        keys.add(key);
      }
      per[w.code] = m;
    }
    return [...keys].sort().map((key) => {
      const row = { key, label: dayjs(`${key}-01`).format('MMM YY') };
      for (const w of sites) {
        const e = per[w.code].get(key);
        if (!e) continue;
        const inv = e.n ? e.avgInventory / e.n : 0;
        // A month with nothing sold, or nothing held, has no ratio — not a
        // ratio of zero. Left out so the line breaks rather than diving.
        row[`${w.code}__inventory`] = inv > 0 ? (e.total / inv) * 100 : null;
        row[`${w.code}__shipped`] = e.cogsSold > 0 ? (e.total / e.cogsSold) * 100 : null;
      }
      return row;
    });
  }, [opex, warehouses, wh]);

  /** The diagonal, spanning whatever the fitted months actually cover. */
  const parity = useMemo(() => {
    const pts = (data?.costModel || []).flatMap((f) => f.points || []);
    if (!pts.length) return [];
    const hi = Math.max(...pts.map((p) => Math.max(p.cost, p.fitted || 0)));
    return [{ fitted: 0, cost: 0 }, { fitted: hi, cost: hi }];
  }, [data]);

  /** What share of the bill each driver accounts for, over whatever is shown. */
  const driverSplit = useMemo(() => {
    const st = opexRows.reduce((t, r) => t + (r.storageRelated || 0), 0);
    const hd = opexRows.reduce((t, r) => t + (r.handlingRelated || 0), 0);
    const fr = opexRows.reduce((t, r) => t + (r.freightRelated || 0), 0);
    const warehousing = st + hd;
    if (warehousing + fr <= 0) return null;
    // The percentages are of warehousing, not of the whole bill. Carriage is
    // not a warehouse charge and including it made Brisbane read as a
    // handling-heavy site when most of that was a truck.
    return {
      storage: st,
      handling: hd,
      freight: fr,
      warehousing,
      storagePct: warehousing > 0 ? Math.round((st / warehousing) * 100) : 0,
      handlingPct: warehousing > 0 ? Math.round((hd / warehousing) * 100) : 0,
      freightPct: Math.round((fr / (warehousing + fr)) * 100),
    };
  }, [opexRows]);

  const opexTotal = useMemo(
    () => opexRows.reduce((t, r) => t + (r.total || 0), 0),
    [opexRows],
  );

  if (error) {
    return <Alert type="error" showIcon message="Could not load inventory" description={error} />;
  }

  const selected = warehouses.find((w) => w.code === wh);
  const perPeriod = wh !== 'all' && opexRows.length
    ? opexTotal / opexRows.length : null;

  return (
    <Spin spinning={loading} tip="Reading stock from Unleashed and the 3PL fee workbook…">
      <div style={{ minHeight: 200 }}>
        <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
          <Space wrap size="middle" align="center">
            <Segmented
              size="small"
              value={wh}
              onChange={setWh}
              options={[
                { label: 'All warehouses', value: 'all' },
                ...warehouses.map((w) => ({
                  label: `${w.code} ${w.name.replace(/ Warehouse$/, '')}`,
                  value: w.code,
                })),
              ]}
            />
            <span style={{ fontSize: 11, color: FAINT }}>
              stock as at {data?.asAt ? dayjs(data.asAt).format('D MMM YYYY HH:mm') : '—'}
            </span>
            {selected && !selected.thirdParty && (
              <Tag color="default" style={{ fontSize: 10 }}>own site — no 3PL fees</Tag>
            )}
          </Space>
        </Card>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}>
            <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
              <Statistic title="Stock on hand" value={totals.value} formatter={(v) => money(v)} valueStyle={{ color: INK }} />
              <div style={{ fontSize: 11, color: FAINT, marginTop: 2 }}>
                {totals.skus} SKUs · {Math.round(totals.units).toLocaleString()} units
              </div>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
              <Statistic
                title={`Not sold in ${data?.deadStockDays || 180} days`}
                value={totals.deadValue}
                formatter={(v) => money(v)}
                valueStyle={{ color: totals.deadValue > 0 ? '#B4531F' : INK }}
              />
              <div style={{ fontSize: 11, color: FAINT, marginTop: 2 }}>
                {totals.deadSkus} SKUs ·{' '}
                {totals.value > 0 ? `${((totals.deadValue / totals.value) * 100).toFixed(1)}% of value` : '—'}
              </div>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
              <Statistic title="Allocated" value={Math.round(totals.allocated)} valueStyle={{ color: INK }} />
              <div style={{ fontSize: 11, color: FAINT, marginTop: 2 }}>
                units spoken for · {Math.round(totals.onPurchase).toLocaleString()} on order
              </div>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
              <Statistic
                title={wh === 'all' ? '3PL cost, all periods' : '3PL cost, all periods'}
                value={opexTotal}
                formatter={(v) => money(v)}
                valueStyle={{ color: '#B4531F' }}
              />
              <div style={{ fontSize: 11, color: FAINT, marginTop: 2 }}>
                {driverSplit
                  ? `warehousing ${driverSplit.storagePct}/${driverSplit.handlingPct} storage/handling`
                    + (driverSplit.freight > 0 ? ` · ${driverSplit.freightPct}% freight` : '')
                  : `${opexRows.length} invoice periods`}
                {perPeriod != null && ` · ${money(perPeriod)} each`}
              </div>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            <Card size="small" style={{ height: '100%' }} title={<span style={{ fontSize: 13 }}>Stock on hand by product (top 15)</span>}>
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={byProduct} margin={{ top: 8, right: 12, left: 8, bottom: 96 }}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis
                    dataKey="code"
                    tick={{ fontSize: 9, fill: NEUTRAL }}
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={96}
                  />
                  <YAxis tick={{ fontSize: 11, fill: NEUTRAL }} tickFormatter={moneyShort} width={60} />
                  <Tooltip
                    cursor={{ fill: 'rgba(47,127,168,0.06)' }}
                    content={({ active, payload }) => (active && payload?.length ? (
                      <Panel
                        title={payload[0].payload.code}
                        rows={[
                          { label: 'Value', value: money(payload[0].payload.value) },
                          { label: 'Units', value: Math.round(payload[0].payload.qty).toLocaleString() },
                        ]}
                        footer={payload[0].payload.name}
                      />
                    ) : null)}
                  />
                  <Bar dataKey="value" name="Stock value" fill="#2F7FA8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>

          <Col xs={24} xl={12}>
            <Card
              size="small"
              style={{ height: '100%' }}
              title={<span style={{ fontSize: 13 }}>Days since last sale vs value held</span>}
              extra={(
                <span style={{ fontSize: 11, color: FAINT }}>
                  log scales ·{' '}
                  <span style={{ color: '#52A373' }}>&lt;30d</span>{' '}
                  <span style={{ color: '#E0A800' }}>30–100d</span>{' '}
                  <span style={{ color: '#D9534F' }}>&gt;100d</span>
                </span>
              )}
            >
              <ResponsiveContainer width="100%" height={360}>
                <ScatterChart margin={{ top: 8, right: 16, left: 8, bottom: 24 }}>
                  <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    dataKey="x"
                    name="Days"
                    scale="log"
                    domain={scatterAxes ? scatterAxes.x : ['auto', 'auto']}
                    ticks={scatterAxes ? scatterAxes.xTicks : undefined}
                    allowDataOverflow
                    tick={{ fontSize: 10, fill: NEUTRAL }}
                    tickFormatter={(v) => `${v}d`}
                    label={{ value: 'Days since last sale (log)', position: 'insideBottom', offset: -14, fontSize: 11, fill: NEUTRAL }}
                  />
                  <YAxis
                    type="number"
                    dataKey="y"
                    name="Value"
                    scale="log"
                    domain={scatterAxes ? scatterAxes.y : ['auto', 'auto']}
                    ticks={scatterAxes ? scatterAxes.yTicks : undefined}
                    allowDataOverflow
                    tick={{ fontSize: 10, fill: NEUTRAL }}
                    tickFormatter={moneyShort}
                    width={60}
                  />
                  <ZAxis range={[48, 48]} />
                  <Tooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ active, payload }) => (active && payload?.length ? (
                      <Panel
                        title={payload[0].payload.code}
                        rows={[
                          { label: 'Value held', value: money(payload[0].payload.y) },
                          { label: 'Units', value: Math.round(payload[0].payload.qty).toLocaleString() },
                          { label: 'Last sold', value: `${payload[0].payload.x} days ago` },
                          { label: 'Warehouse', value: payload[0].payload.warehouse },
                        ]}
                        footer={payload[0].payload.name}
                      />
                    ) : null)}
                  />
                  <Scatter data={scatter} fill="#2F7FA8">
                    {scatter.map((s) => <Cell key={`${s.warehouse}-${s.code}`} fill={s.fill} />)}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
              <div style={{ fontSize: 11, color: FAINT, marginTop: 4 }}>
                Stock with no value and products that have never sold are left out:
                neither can be placed on a log scale, and pinning them to a floor
                would invent a position.
              </div>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} xl={12}>
            <Card
              size="small"
              style={{ height: '100%' }}
              title={<span style={{ fontSize: 13 }}>Warehouse cost over time</span>}
              extra={(
                <Space size={8}>
                  <span style={{ fontSize: 11, color: FAINT }}>ex GST</span>
                  <Segmented
                    size="small"
                    value={costMode}
                    onChange={setCostMode}
                    options={[
                      { label: 'By driver', value: 'driver' },
                      { label: 'By charge', value: 'charge' },
                    ]}
                  />
                </Space>
              )}
            >
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={months} margin={{ top: 8, right: 12, left: 8, bottom: 44 }}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: NEUTRAL }}
                    angle={-45}
                    textAnchor="end"
                    height={44}
                    interval={months.length > 18 ? 1 : 0}
                  />
                  <YAxis tick={{ fontSize: 11, fill: NEUTRAL }} tickFormatter={moneyShort} width={56} />
                  <Tooltip
                    cursor={{ fill: 'rgba(47,127,168,0.06)' }}
                    content={({ active, payload }) => (active && payload?.length ? (
                      <Panel
                        title={payload[0].payload.label}
                        rows={[
                          ...(costMode === 'driver' ? DRIVERS : BUCKETS).map(([k, l, c]) => ({
                            label: l, value: money(payload[0].payload[k]), color: c,
                          })),
                          { label: 'Total', value: money(payload[0].payload.total) },
                        ]}
                        footer={`${payload[0].payload.periods} invoice period${payload[0].payload.periods === 1 ? '' : 's'}`
                          + (payload[0].payload.inferred ? ' · month inferred from the invoice label' : '')}
                      />
                    ) : null)}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {(costMode === 'driver' ? DRIVERS : BUCKETS).map(([k, l, c]) => (
                    <Bar key={k} dataKey={k} name={l} stackId="a" fill={c} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
              <div style={{ fontSize: 11, color: FAINT, marginTop: 4 }}>
                {costMode === 'driver' ? (
                  <>
                    Every charge on the invoice assigned to what causes it: storage,
                    stock takes and the monthly site charges accrue while stock sits;
                    picking, fork lifts, inbound and outbound accrue when it moves.
                    Freight is kept out of both — it buys a truck, not a shelf or the
                    labour at it, and the workbook already draws that line itself. A
                    discount is spread across all three in the proportion they were
                    charged, being a rebate on the bill and not on one line of it.
                    Assigned on what each charge is for, not on how it correlates —
                    that was tested and does not separate them.
                    {driverSplit && (
                      <>
                        {' '}Over the periods shown, warehousing splits
                        {' '}{driverSplit.storagePct}% storage to {driverSplit.handlingPct}%
                        {' '}handling, with {money(driverSplit.freight)} of freight beside it.
                      </>
                    )}
                  </>
                ) : (
                  <>
                    The provider&rsquo;s own categories. Every charge lands in one of
                    these four and they add to the total the sheet states.
                  </>
                )}
                {' '}Summed into calendar months, the only axis the three contracts
                share — they invoice weekly, monthly and on a four-week cycle.
              </div>
            </Card>
          </Col>

          <Col xs={24} xl={12}>
            <Card
              size="small"
              style={{ height: '100%' }}
              title={<span style={{ fontSize: 13 }}>Cost per dollar of {ratioMode === 'inventory' ? 'inventory held' : 'stock shipped'}</span>}
              extra={(
                <Segmented
                  size="small"
                  value={ratioMode}
                  onChange={setRatioMode}
                  options={[
                    { label: 'Inventory', value: 'inventory' },
                    { label: 'Shipped', value: 'shipped' },
                  ]}
                />
              )}
            >
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={ratioSeries} margin={{ top: 8, right: 12, left: 8, bottom: 44 }}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: NEUTRAL }}
                    angle={-45}
                    textAnchor="end"
                    height={44}
                    interval={ratioSeries.length > 18 ? 1 : 0}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: NEUTRAL }}
                    tickFormatter={(v) => `${v}%`}
                    width={52}
                    domain={['auto', 'auto']}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => (active && payload?.length ? (
                      <Panel
                        title={label}
                        rows={payload.map((pp) => ({
                          label: pp.name,
                          value: pp.value == null ? null : `${pp.value.toFixed(2)}%`,
                          color: pp.color,
                        }))}
                        footer={ratioMode === 'inventory'
                          ? 'Total 3PL charge against the average inventory held that month — what it costs to keep a dollar on the shelf.'
                          : 'Total 3PL charge against the cost of goods shipped that month — what it costs to move a dollar out.'}
                      />
                    ) : null)}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {warehouses.filter((w) => w.thirdParty && (wh === 'all' || w.code === wh)).map((w) => (
                    <Line
                      key={w.code}
                      type="monotone"
                      dataKey={`${w.code}__${ratioMode}`}
                      name={w.name.replace(/ Warehouse$/, '')}
                      stroke={WH_COLOR[w.code]}
                      strokeWidth={2}
                      dot={{ r: 2 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              <div style={{ fontSize: 11, color: FAINT, marginTop: 4 }}>
                Storage is paid on what is held and handling on what goes out, so the
                same total has two denominators and which one is right depends on the
                question. A month with nothing held or nothing shipped has no ratio
                rather than a ratio of zero, so the line breaks instead of diving.
              </div>
            </Card>
          </Col>
        </Row>

        <Card
          size="small"
          style={{ marginTop: 16 }}
          title={<span style={{ fontSize: 13 }}>What drives each warehouse bill</span>}
          extra={(
            <span style={{ fontSize: 11, color: FAINT }}>
              warehousing cost = w1 x inventory held + w2 x goods shipped, by month
            </span>
          )}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={9}>
              {(data?.costModel || []).map((f) => (
                <div
                  key={f.code}
                  style={{
                    padding: '10px 0',
                    borderBottom: '1px solid #F0F2F5',
                    opacity: (wh === 'all' || wh === f.code) ? 1 : 0.4,
                  }}
                >
                  <Space size={6} style={{ marginBottom: 4 }}>
                    <span style={{
                      width: 8, height: 8, borderRadius: 2, display: 'inline-block',
                      background: WH_COLOR[f.code],
                    }}
                    />
                    <span style={{ color: INK, fontWeight: 500 }}>{f.name.replace(/ Warehouse$/, '')}</span>
                    <span style={{ fontSize: 11, color: FAINT }}>{f.n} months</span>
                  </Space>
                  {f.unfittable ? (
                    <div style={{ fontSize: 11, color: FAINT, paddingLeft: 14 }}>
                      too few months, or the two drivers move together too closely to be
                      told apart
                    </div>
                  ) : (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'auto auto auto',
                      gap: '2px 20px',
                      fontSize: 11,
                      color: NEUTRAL,
                      paddingLeft: 14,
                    }}
                    >
                      <span>per $ held</span>
                      <span>per $ shipped</span>
                      <span>R2</span>
                      <b style={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>
                        {f.storageRatePct.toFixed(2)}%
                      </b>
                      <b style={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>
                        {f.handlingRatePct.toFixed(2)}%
                      </b>
                      <b style={{
                        color: f.r2 >= 0.6 ? INK : '#B4531F',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                      >
                        {f.r2 == null ? '\u2014' : f.r2.toFixed(3)}
                      </b>
                    </div>
                  )}
                </div>
              ))}
              <div style={{ fontSize: 11, color: FAINT, marginTop: 10, lineHeight: 1.7 }}>
                Fitted through the origin, so there is no intercept to absorb what the
                two drivers fail to explain. A fixed monthly charge, where one exists,
                therefore shows up as a lower R2 rather than hiding inside a constant,
                which is the honest place for it.
              </div>
            </Col>

            <Col xs={24} lg={15}>
              <ResponsiveContainer width="100%" height={340}>
                <ScatterChart margin={{ top: 8, right: 16, left: 8, bottom: 28 }}>
                  <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    dataKey="fitted"
                    name="Predicted"
                    tick={{ fontSize: 10, fill: NEUTRAL }}
                    tickFormatter={moneyShort}
                    label={{
                      value: 'Predicted by the model', position: 'insideBottom',
                      offset: -18, fontSize: 11, fill: NEUTRAL,
                    }}
                  />
                  <YAxis
                    type="number"
                    dataKey="cost"
                    name="Actual"
                    tick={{ fontSize: 10, fill: NEUTRAL }}
                    tickFormatter={moneyShort}
                    width={62}
                  />
                  <ZAxis range={[46, 46]} />
                  <Tooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ active, payload }) => (active && payload?.length ? (
                      <Panel
                        title={`${payload[0].payload.site} - ${payload[0].payload.month}`}
                        rows={[
                          { label: 'Invoiced', value: money(payload[0].payload.cost) },
                          { label: 'Predicted', value: money(payload[0].payload.fitted) },
                          { label: 'Inventory held', value: money(payload[0].payload.inventory) },
                          { label: 'Goods shipped', value: money(payload[0].payload.shipped) },
                        ]}
                        footer="Above the diagonal the bill ran over what the two drivers account for; below, under."
                      />
                    ) : null)}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {/*
                    * The diagonal is the model being exactly right. A series
                    * rather than a reference line so it scales with the axes,
                    * and dashed so it never reads as data.
                    */}
                  <Scatter
                    name="perfect fit"
                    data={parity}
                    line={{ stroke: '#C9D2DC', strokeDasharray: '4 4' }}
                    shape={() => null}
                    legendType="plainline"
                  />
                  {(data?.costModel || []).filter((f) => !f.unfittable).map((f) => (
                    <Scatter
                      key={f.code}
                      name={`${f.name.replace(/ Warehouse$/, '')} R2 ${f.r2 == null ? '\u2014' : f.r2.toFixed(2)}`}
                      data={f.points.map((pt) => ({ ...pt, site: f.name.replace(/ Warehouse$/, '') }))}
                      fill={WH_COLOR[f.code]}
                      fillOpacity={(wh === 'all' || wh === f.code) ? 0.85 : 0.15}
                    />
                  ))}
                </ScatterChart>
              </ResponsiveContainer>
              <div style={{ fontSize: 11, color: FAINT, marginTop: 4 }}>
                Two drivers cannot be drawn as a line on two axes, so the fit is shown
                against itself: every month at what it was invoiced against what the
                model says it should have been. The dashed diagonal is a perfect fit.
                How tightly a site hugs it is its R2; which side it sits on says whether
                the bill ran over or under what the drivers explain.
              </div>
            </Col>
          </Row>
        </Card>

        <Card
          size="small"
          style={{ marginTop: 16 }}
          bodyStyle={{ padding: 0 }}
          title={<span style={{ fontSize: 13 }}>Stock by warehouse</span>}
        >
          <Table
            size="small"
            pagination={false}
            rowKey="code"
            dataSource={warehouses}
            columns={[
              { title: 'Warehouse',
                dataIndex: 'name',
                key: 'name',
                render: (v, r) => (
                  <Space size={6}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, display: 'inline-block', background: WH_COLOR[r.code] }} />
                    <span style={{ color: INK }}>{v}</span>
                    {!r.thirdParty && <Tag style={{ fontSize: 10 }}>own site</Tag>}
                  </Space>
                ) },
              { title: 'SKUs', dataIndex: 'skus', key: 'skus', align: 'right' },
              { title: 'Value', dataIndex: 'value', key: 'value', align: 'right', render: (v) => <b>{money(v)}</b> },
              /*
               * Allocated and on order in money, not units.
               *
               * A unit of inverter and a unit of roof bracket are both "1", so
               * a count said which site stocks small parts and nothing about
               * what is committed — Greenacre's 66,174 units against
               * Brisbane's 637 compares nothing at all. The unit counts are
               * still there on hover.
               */
              { title: 'Allocated',
                dataIndex: 'allocatedValue',
                key: 'allocatedValue',
                align: 'right',
                render: (v, r) => (
                  <AntTooltip title={`${Math.round(r.allocated).toLocaleString()} units`}>
                    <span>{money(v)}</span>
                  </AntTooltip>
                ) },
              { title: 'On order',
                dataIndex: 'onPurchaseValue',
                key: 'onPurchaseValue',
                align: 'right',
                render: (v, r) => (
                  <AntTooltip title={`${Math.round(r.onPurchase).toLocaleString()} units`}>
                    <span>{money(v)}</span>
                  </AntTooltip>
                ) },
              { title: `Idle >${data?.deadStockDays || 180}d`,
                dataIndex: 'deadValue',
                key: 'deadValue',
                align: 'right',
                render: (v, r) => (
                  <AntTooltip title={`${r.deadSkus} SKUs with no sale in ${data?.deadStockDays || 180} days`}>
                    <span style={{ color: v > 0 ? '#B4531F' : FAINT }}>{money(v)}</span>
                  </AntTooltip>
                ) },
            ]}
          />
        </Card>

        {data?.opexProblems?.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
            message="Part of the 3PL fee workbook could not be read"
            description={data.opexProblems.join(' · ')}
          />
        )}
      </div>
    </Spin>
  );
}
